import {
  BarChart3,
  FolderTree,
  GanttChartSquare,
  Globe2,
  LineChart,
  Map as MapIcon,
  Orbit,
  RotateCcw,
  Rocket,
  Route,
  SlidersHorizontal,
  Wand2,
  type LucideIcon,
} from 'lucide-react';
import {
  cn,
  PANEL_DESCRIPTIONS,
  PANEL_TITLES,
  type PanelId,
} from '@trajectory/ui';
import { resetLayout, togglePanel, useIsPanelOpen } from '@/stores/layoutStore';

const PANEL_ICONS: Record<PanelId, LucideIcon> = {
  'mission-explorer': FolderTree,
  'rocket-editor': Rocket,
  'orbit-editor': Orbit,
  'viewport-3d': Globe2,
  'map-2d': MapIcon,
  charts: LineChart,
  timeline: GanttChartSquare,
  inspector: SlidersHorizontal,
  analytics: BarChart3,
  optimizer: Wand2,
  'transfer-planner': Route,
};

function PanelToggleButton({ id }: { id: PanelId }) {
  const Icon = PANEL_ICONS[id];
  const open = useIsPanelOpen(id);

  return (
    <button
      type="button"
      onClick={() => togglePanel(id)}
      title={`${PANEL_TITLES[id]} — ${PANEL_DESCRIPTIONS[id]}`}
      aria-label={`${open ? 'Close' : 'Open'} ${PANEL_TITLES[id]} panel`}
      aria-pressed={open}
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded-control text-muted hover:bg-surface-sunken hover:text-text',
        open && 'bg-surface-sunken text-accent',
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

/** Panel-visibility toggles plus layout reset, always visible beneath the title bar. */
export function CommandBar() {
  return (
    <div
      className="flex items-center gap-1 border-b border-border bg-surface px-2"
      style={{ height: 'var(--toolbar-height)' }}
    >
      {(Object.keys(PANEL_ICONS) as PanelId[]).map((id) => (
        <PanelToggleButton key={id} id={id} />
      ))}
      <div className="mx-1 h-4 w-px bg-border" />
      <button
        type="button"
        onClick={() => resetLayout()}
        title="Reset panel layout"
        aria-label="Reset panel layout"
        className="flex h-7 w-7 items-center justify-center rounded-control text-muted hover:bg-surface-sunken hover:text-text"
      >
        <RotateCcw className="h-4 w-4" />
      </button>
    </div>
  );
}
